import { Badge, Button, Progress } from '@/components/ui'
import { TableCell, TableRow } from '@/components/ui/table'
import { formatDateTime, formatDurationMs, formatElapsedTime } from '@/lib/timeFormatters'
import { cn, getInitials } from '@/lib/utils'
import type { ChainRunSummary } from '@/types'
import { Eye, Link2, Play } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { LinkedEntityBadge, WorkflowStatusBadge } from '../../workflows/shared'
import { AutomaticStartBadge } from './AutomaticStartBadge'
import { chainRunProgressColorClasses } from './chainRunStatusConfig'

interface Props {
  run: ChainRunSummary
}

export function ChainRunRow({ run }: Props) {
  const navigate = useNavigate()
  const { projectId } = useParams()

  const detailPath = `/projects/${projectId}/workflows/runs/chains/${run.id}`

  const isResumable = run.status === 'AwaitingInputs' || run.status === 'AwaitingManualStart'
  const showErrorSummary = (isResumable || run.status === 'Failed') && !!run.errorSummary

  const linkProgress = run.totalLinks > 0 ? (run.completedLinks / run.totalLinks) * 100 : 0
  const duration = run.completedAt == null && run.startedAt
    ? formatElapsedTime(run.startedAt, run.completedAt)
    : formatDurationMs(run.durationMs)

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation()
    action()
  }

  return (
    <TableRow
      className="cursor-pointer"
      onClick={() => navigate(detailPath)}
      data-testid="chain-run-row"
    >
      <TableCell>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-primary-foreground shrink-0">
            <Link2 className="h-4 w-4" />
          </div>
          <span className="font-medium truncate">{run.chainDisplayName}</span>
          <Badge variant="outline" className="text-xs shrink-0 border-border-accent">v{run.definitionVersion}</Badge>
        </div>
      </TableCell>

      <TableCell>
        <LinkedEntityBadge type={run.linkedEntityType} number={run.linkedEntityNumber} entityId={run.linkedEntityId} available={run.linkedEntityAvailable} status={run.linkedEntityStatus} />
      </TableCell>

      <TableCell>
        <WorkflowStatusBadge status={run.status} />
        {showErrorSummary && (
          <div
            className="mt-1 text-xs text-muted-foreground line-clamp-2"
            title={run.errorSummary ?? undefined}
            data-testid="chain-run-error-summary"
          >
            {run.errorSummary}
          </div>
        )}
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-2">
          <Progress value={linkProgress} className="h-2 flex-1" indicatorClassName={chainRunProgressColorClasses[run.status]} />
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {run.completedLinks}/{run.totalLinks}
          </span>
        </div>
        {(run.status === 'Running' || isResumable) && run.currentLinkName && (
          <div className={cn('text-xs mt-1 truncate', run.status === 'Running' ? 'text-info-foreground' : 'text-warning-foreground')}>
            {run.currentLinkName}
          </div>
        )}
      </TableCell>

      <TableCell className="text-card-foreground">
        {formatDateTime(run.startedAt)}
      </TableCell>

      <TableCell className="text-card-foreground">
        {duration}
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0 bg-slate-700">
            {getInitials(run.triggeredBy)}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium truncate">{run.triggeredBy}</span>
              <AutomaticStartBadge triggerSource={run.triggerSource} />
            </div>
            {run.triggeredByEmail && (
              <div className="text-xs text-card-foreground truncate">
                {run.triggeredByEmail}
              </div>
            )}
          </div>
        </div>
      </TableCell>

      <TableCell onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            aria-label="View chain run"
            onClick={(e) => handleActionClick(e, () => navigate(detailPath))}
            className="text-foreground"
          >
            <Eye className="h-4 w-4 mr-1 text-accent-soft" /> View
          </Button>
          {isResumable && (
            <Button
              size="sm"
              variant="ghost"
              aria-label="Open chain run to resume"
              onClick={(e) => handleActionClick(e, () => navigate(detailPath))}
              className="text-foreground"
            >
              <Play className="h-4 w-4 mr-1 text-accent-soft" /> Open
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}
